import { format, parseISO } from 'date-fns'
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CardBoard } from '@/components/shared/card-board'
import { useResizableColumns } from '@/hooks/use-resizable-columns'
import { useResizableRow } from '@/hooks/use-resizable-row'
import {
  readLocalPreference,
  writeLocalPreference,
} from '@/lib/local-preference'
import { FlashNote } from '../shared/flash-note'
import { useCoachingStore } from '../store'
import type { ScheduleDay, TrayCard } from './_lib/auto-place'
import { buildMoveTargets } from './_lib/move-targets'
import type { CardDetailsSaveResult } from './_state/contracts'
import { LibraryCard } from './library/library-card'
import { Schedule } from './schedule/schedule'
import { DragCardFace } from './schedule/schedule-card'
import { CardDetailsModal } from './shared/card-details-modal'
import { RelatedProblemModal } from './shared/related-problem-modal'
import { StagingTray } from './tray/staging-tray'

/**
 * The related-problem modal's writes (LG-360 §8) — staging to the tray,
 * assigning alongside the current card, and swapping it in — are real slice
 * methods now (`curriculum.stageRelatedProblem` /
 * `curriculum.replaceWithRelatedProblem`), but each is optional: the mock
 * (non-persisted) slice doesn't implement either. This is the fallback for
 * that case, and for a related problem or open card the tab can't currently
 * resolve. Returning it rather than a silent no-op or a fabricated success
 * is the same honesty `CompletionControl`'s "Overriding a platform-tracked
 * task is not built yet." already uses in this modal family: the coach sees
 * a real failure, not a click that did nothing and said nothing.
 */
const RELATED_WRITE_NOT_BUILT: CardDetailsSaveResult = {
  error: 'Not built yet — this write is coming in a follow-up.',
}

/**
 * Resolved routing decision for a drag-and-drop card placement, keyed by
 * whether the dragged card currently lives in the tray (a plain place) or
 * on a schedule day (a move). Pure + exported so the decision is
 * unit-testable without simulating @dnd-kit pointer events.
 */
export type CurriculumDropRouting =
  | { kind: 'place'; cardId: string; weekIdx: number; dayIdx: number }
  | { kind: 'move'; cardId: string; weekIdx: number; dayIdx: number }
  | null

export interface CurriculumDropSnapshot {
  tray: readonly { id: string }[]
  weeks: readonly { id: string }[]
}

/**
 * Parses a `${weekId}::${dayIdx}` slot id and decides whether the dropped
 * card should be placed from the tray or moved from another day, by
 * looking the card up in the snapshot — `onPlace` never receives the drag
 * `data`, so this is the only source of truth for "where does it live now".
 */
export function resolveCurriculumDrop(
  cardId: string,
  slotId: string,
  snapshot: CurriculumDropSnapshot,
): CurriculumDropRouting {
  const [weekId, dayIdxRaw] = slotId.split('::')
  const dayIdx = Number(dayIdxRaw)
  if (!weekId || dayIdxRaw === undefined || Number.isNaN(dayIdx)) return null
  const weekIdx = snapshot.weeks.findIndex((week) => week.id === weekId)
  if (weekIdx < 0) return null
  const isTrayCard = snapshot.tray.some((card) => card.id === cardId)
  return isTrayCard
    ? { kind: 'place', cardId, weekIdx, dayIdx }
    : { kind: 'move', cardId, weekIdx, dayIdx }
}

/**
 * Finds a card anywhere on the board and, when it is scheduled, the date it
 * sits on. `assignedDateLabel` is null for a tray card — it has no date yet,
 * which the modal says in words rather than leaving blank.
 */
export function findDetailsCard(
  board: {
    tray: readonly TrayCard[]
    weeks: readonly { days: readonly ScheduleDay[] }[]
  },
  cardId: string,
): { card: TrayCard; assignedDateLabel: string | null } | null {
  const trayCard = board.tray.find((card) => card.id === cardId)
  if (trayCard) return { card: trayCard, assignedDateLabel: null }
  for (const week of board.weeks) {
    for (const day of week.days) {
      const found = day.cards.find((card) => card.id === cardId)
      if (!found) continue
      return {
        card: found,
        // `dateKey` is a plain YYYY-MM-DD, so it parses to local midnight and
        // formats without a timezone shifting the day. Days synthesised by the
        // mock board carry only a short label, which is better than nothing.
        assignedDateLabel: day.dateKey
          ? format(parseISO(day.dateKey), 'EEEE, MMMM d, yyyy')
          : day.label,
      }
    }
  }
  return null
}

const SPLIT_STORAGE_KEY = 'bctci:curriculum:library-split'
const DEFAULT_LIBRARY_PCT = 80
// The tray may be dragged very small so the library can take nearly the
// full width, but never so small that its drag handle becomes unreachable.
const LIBRARY_MIN_PCT = 30
const LIBRARY_MAX_PCT = 96

const storedLibraryPct = (): number =>
  readLocalPreference(
    SPLIT_STORAGE_KEY,
    (raw) => {
      const parsed = Number(raw)
      if (!Number.isFinite(parsed)) return null
      return Math.min(LIBRARY_MAX_PCT, Math.max(LIBRARY_MIN_PCT, parsed))
    },
    DEFAULT_LIBRARY_PCT,
  )

const ROW_STORAGE_KEY = 'bctci:curriculum:library-height'
// 480px is what the region measures today with the browse table at its old
// fixed 330px cap, so an untouched dashboard looks exactly as it did.
const DEFAULT_LIBRARY_PX = 480
// Measured, not guessed: the library card's own chrome is ~245px with the
// browse tab's search, preset and company rows showing, and a table row is
// ~37px, so this floor is the point below which the table stops showing two
// whole rows and starts showing a sliver of one.
const LIBRARY_MIN_PX = 340
const LIBRARY_MAX_PX = 1200
// Whatever the ceiling, leave this much viewport for the schedule and the
// divider itself — otherwise a height carried over from a taller monitor puts
// its own drag handle below the fold, where it cannot be dragged back.
const SCHEDULE_RESERVE_PX = 220

/**
 * The tallest the library/tray region may be on *this* viewport. Read once at
 * mount rather than on every resize: a mid-session window resize leaves a
 * too-tall region, which the coach can simply drag back, whereas re-clamping
 * live would silently discard a height they chose on purpose.
 */
const libraryMaxPx = (): number => {
  // `null` rather than 0 for "no window": a real `innerHeight` of 0 (a hidden
  // iframe, a window measured before layout) would otherwise be read as SSR
  // and hand back the widest ceiling — the one case the reserve exists for.
  const viewport = typeof window === 'undefined' ? null : window.innerHeight
  if (viewport === null) return LIBRARY_MAX_PX
  return Math.max(
    LIBRARY_MIN_PX,
    Math.min(LIBRARY_MAX_PX, viewport - SCHEDULE_RESERVE_PX),
  )
}

const storedLibraryPx = (max: number): number =>
  readLocalPreference(
    ROW_STORAGE_KEY,
    (raw) => {
      const parsed = Number(raw)
      if (!Number.isFinite(parsed)) return null
      return Math.min(max, Math.max(LIBRARY_MIN_PX, parsed))
    },
    Math.min(max, DEFAULT_LIBRARY_PX),
  )

const PERSIST_DEBOUNCE_MS = 250

/**
 * Write-through of a splitter position to local storage, debounced so a drag
 * costs one write instead of one per pointer frame.
 *
 * Skips the mount pass, so hydration never rewrites the value it just read,
 * and flushes a still-owed write on unmount: the dashboard unmounts this tab
 * outright when the coach switches to Overview or Review, so a cancel-only
 * cleanup would silently discard a divider they moved a moment earlier.
 */
function usePersistedSplit(key: string, value: number): void {
  const hydrating = useRef(true)
  const timer = useRef<number | null>(null)
  const unwritten = useRef<number | null>(null)

  useEffect(() => {
    if (hydrating.current) {
      hydrating.current = false
      return
    }
    unwritten.current = value
    timer.current = window.setTimeout(() => {
      writeLocalPreference(key, String(value))
      unwritten.current = null
      timer.current = null
    }, PERSIST_DEBOUNCE_MS)
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
  }, [key, value])

  // Declared second so its cleanup runs after the one above has cancelled the
  // timer, leaving this the only writer during teardown.
  useEffect(
    () => () => {
      if (unwritten.current === null) return
      writeLocalPreference(key, String(unwritten.current))
      unwritten.current = null
    },
    [key],
  )
}

export function CurriculumTab(props: { onOpenReview?: () => void }): ReactNode {
  const { onOpenReview = () => undefined } = props
  const { curriculum } = useCoachingStore()
  // Read storage once at mount; the hook owns the width state from there.
  const [initialLibraryPct] = useState(storedLibraryPct)
  const { widths, getResizeHandleProps, containerRef } = useResizableColumns({
    columns: [
      {
        id: 'library',
        initialWidth: initialLibraryPct,
        minWidth: LIBRARY_MIN_PCT,
        maxWidth: LIBRARY_MAX_PCT,
      },
      {
        id: 'tray',
        initialWidth: 100 - initialLibraryPct,
        minWidth: 100 - LIBRARY_MAX_PCT,
        maxWidth: 100 - LIBRARY_MIN_PCT,
      },
    ],
  })

  // The row splitter's ceiling is viewport-derived, so it is captured at mount
  // alongside the stored height it clamps.
  const [libraryMax] = useState(libraryMaxPx)
  const [initialLibraryPx] = useState(() => storedLibraryPx(libraryMax))
  const { height: libraryHeight, getResizeHandleProps: getRowHandleProps } =
    useResizableRow({
      initialHeight: initialLibraryPx,
      minHeight: LIBRARY_MIN_PX,
      maxHeight: libraryMax,
    })

  const [detailsCardId, setDetailsCardId] = useState<string | null>(null)
  // Which related problem the secondary modal (§8) is open on — a chip in
  // the card-details modal's header sets this, the tab looks the row up in
  // `curriculum.cardDetails?.related` rather than holding the row itself, for
  // the same reason `detailsCardId` holds an id and not a card: a write can
  // refetch the details out from under an open dialog.
  const [relatedProblemId, setRelatedProblemId] = useState<string | null>(null)

  // Persist after each drag settles instead of once per pointer frame.
  usePersistedSplit(SPLIT_STORAGE_KEY, widths.library)
  usePersistedSplit(ROW_STORAGE_KEY, libraryHeight)

  // The floating overlay renders a faithful, full-size copy of the dragged
  // card (matching the homework board), not a shrunken title pill — otherwise
  // the card visibly changes size the instant it is picked up. Look the card
  // up across tray + weeks rather than trusting drag data (which onPlace never
  // receives either), and remember where it lives so the overlay picks the
  // matching face (tray article vs schedule card).
  const findDragCard = (
    cardId: string,
  ): { card: TrayCard; source: 'tray' | 'schedule' } | null => {
    const trayCard = curriculum.tray.find((card) => card.id === cardId)
    if (trayCard) return { card: trayCard, source: 'tray' }
    for (const week of curriculum.weeks) {
      for (const day of week.days) {
        const found = day.cards.find((card) => card.id === cardId)
        if (found) return { card: found, source: 'schedule' }
      }
    }
    return null
  }

  // Which card the details modal is showing. It holds the id, not the card:
  // the board refetches after every write, and a captured object would keep
  // rendering the note the coach just replaced.
  const detailsCard =
    detailsCardId === null ? null : findDetailsCard(curriculum, detailsCardId)

  // Tell the slice which problem the modal is showing, so the persisted store
  // can fetch its history, sources and relatives. A card with no problem
  // behind it (a one-off card) reports null — there is nothing to ask about.
  const detailsProblemId = detailsCard?.card.problemId ?? null
  useEffect(() => {
    curriculum.setDetailsProblemId?.(detailsProblemId)
  }, [curriculum.setDetailsProblemId, detailsProblemId])

  // And which card's review it is showing (§3). The card id, not the problem
  // id above: a review hangs off the assignment, so a card with no catalog
  // problem behind it — every system-design and behavioral card — still has
  // one to ask for. `detailsCardId`, not `detailsCard?.card.id`: the lookup
  // misses whenever a refetch drops the page the card sat on, and reading it
  // would cancel the review fetch for a dialog that is still open on that
  // card (`open` is derived from the id for the same reason).
  useEffect(() => {
    curriculum.setDetailsAssignmentId?.(detailsCardId)
  }, [curriculum.setDetailsAssignmentId, detailsCardId])

  // Which days the open card may be assigned to (LG-360 §4). Derived rather
  // than held in state: a placement rewrites the board, and a captured list
  // would keep offering the day the card has just left as its current one.
  const moveTargets = detailsCard
    ? buildMoveTargets(
        curriculum.weeks,
        detailsCard.card,
        format(new Date(), 'yyyy-MM-dd'),
      )
    : []

  /**
   * Re-dates the open card. A tray card has no day yet, so it is a `place`;
   * anything already on the board is a `move` — the same distinction
   * `resolveCurriculumDrop` makes for a drag, made here from the tray rather
   * than from drag data, which is equally the only source of truth available.
   */
  const onAssignToDay = (weekIdx: number, dayIdx: number): void => {
    if (detailsCardId === null) return
    const inTray = curriculum.tray.some((card) => card.id === detailsCardId)
    if (inTray) {
      curriculum.placeCardAt(detailsCardId, weekIdx, dayIdx)
      return
    }
    curriculum.moveCardTo(detailsCardId, weekIdx, dayIdx)
  }

  // The related problem the secondary modal (§8) is open on, looked up in the
  // fetch already driving the card-details modal — there is no second fetch
  // for it. Null whenever there is no id to look up, or the details fetch the
  // id came from has since moved on to a different card (a write refetches
  // the details and can drop rows a still-open related modal was showing).
  const relatedProblem =
    relatedProblemId === null
      ? null
      : (curriculum.cardDetails?.related.find(
          (item) => item.problemId === relatedProblemId,
        ) ?? null)

  /**
   * `RelatedProblemModal`'s React `key` (LG-360 §8, fix-rounds 1 and 2) —
   * two independent axes have to reset its `choice`/error state, and only
   * one of them is "which related problem is this".
   *
   * `relatedModalLastIdRef` holds the *last non-null* `relatedProblemId`,
   * not `relatedProblem?.problemId ?? 'none'`: that simpler version would
   * also change on every *close* (id → `'none'`), remounting the component
   * at exactly the moment it needs to keep rendering its own
   * `lastOpen`-snapshotted content through the 200ms exit animation — a
   * fresh instance's `lastOpen.current` starts `null`, so it would return
   * `null` immediately instead of fading out, the same "flash empty on
   * close" this whole snapshot idiom exists to prevent. Closing (id → null)
   * leaves this ref unchanged; only a genuinely different related problem
   * opening changes it.
   *
   * That alone under-fixes a second case, found in fix-round 2: reopening
   * the *same* related problem after closing it (say, after a failed
   * assign) must also start fresh — a stale unconfirmed day choice is
   * exactly the hazard §4's own picker is keyed against, and re-showing a
   * previous attempt's error with no way for the coach to tell it is old is
   * simply wrong. The id alone can't catch that, since the id doesn't
   * change. `relatedModalOpenSeqRef` is a counter that bumps on every
   * `null → non-null` transition (tracked via `relatedModalWasOpenRef`,
   * compared against on this render, not "did I re-render" — so it is
   * idempotent under a duplicate render of the same commit, e.g. Strict
   * Mode); folded into the key alongside the id, it forces a fresh instance
   * on *every* open, same problem or not, while still never bumping on
   * close — the exit-animation snapshot stays intact either way.
   *
   * All three refs mutated unconditionally during render, computed and
   * consumed in this same render — the same idiom `lastOpen.current =
   * snapshot` already uses in both `card-details-modal.tsx` and
   * `related-problem-modal.tsx`, just with the extra bookkeeping a second
   * axis needs.
   */
  const relatedModalLastIdRef = useRef<string>('none')
  const relatedModalOpenSeqRef = useRef(0)
  const relatedModalWasOpenRef = useRef(false)
  const relatedModalIsOpenNow = relatedProblemId !== null
  if (relatedModalIsOpenNow) {
    relatedModalLastIdRef.current = relatedProblemId
    if (!relatedModalWasOpenRef.current) relatedModalOpenSeqRef.current += 1
  }
  relatedModalWasOpenRef.current = relatedModalIsOpenNow
  const relatedModalKey = `${relatedModalLastIdRef.current}:${relatedModalOpenSeqRef.current}`

  // Every day the related problem could be assigned to, built the same way
  // `moveTargets` above is — except the related problem has no card of its
  // own on the board yet, so `buildMoveTargets` is fed a synthetic one. Its
  // `id: ''` cannot match any card on any day, so `current` is `false` for
  // every day it returns; that is correct, not a placeholder — an unassigned
  // problem sits on no day.
  const relatedMoveTargets = relatedProblem
    ? buildMoveTargets(
        curriculum.weeks,
        {
          id: '',
          title: relatedProblem.title,
          type: 'coding',
          meta: '',
          // No card exists yet to read a real estimate from. 30m mirrors the
          // staging tray's own fallback for an unestimated card — filler for
          // the advisory over-budget flag, never a value that gets persisted.
          est: (relatedProblem.estimatedMinutes ?? 30) / 60,
        },
        format(new Date(), 'yyyy-MM-dd'),
      )
    : []

  /**
   * The related-problem modal's writes (LG-360 §8), backed by the slice
   * methods Task 11 lands. `curriculum.stageRelatedProblem` /
   * `curriculum.replaceWithRelatedProblem` are optional — the mock
   * (non-persisted) slice doesn't implement them — so each falls back to
   * `RELATED_WRITE_NOT_BUILT` when the method or the related problem itself
   * (or, for a swap, the open card) isn't there. A stub that resolved
   * `{ error: null }` in that case would tell the modal the write succeeded
   * when nothing happened; leaving the buttons unwired entirely would make a
   * click that does nothing report nothing either.
   */
  const onAddRelatedToTray = async (): Promise<CardDetailsSaveResult> => {
    if (!relatedProblem?.learningItemId || !curriculum.stageRelatedProblem) {
      return RELATED_WRITE_NOT_BUILT
    }
    return curriculum.stageRelatedProblem({
      learningItemId: relatedProblem.learningItemId,
      catalogId: relatedProblem.problemId,
      title: relatedProblem.title,
      estimatedMinutes: relatedProblem.estimatedMinutes,
      eloRating: relatedProblem.eloRating,
      target: 'tray',
    })
  }

  const onAssignRelatedToDay = async (
    weekIdx: number,
    dayIdx: number,
  ): Promise<CardDetailsSaveResult> => {
    if (!relatedProblem?.learningItemId || !curriculum.stageRelatedProblem) {
      return RELATED_WRITE_NOT_BUILT
    }
    return curriculum.stageRelatedProblem({
      learningItemId: relatedProblem.learningItemId,
      catalogId: relatedProblem.problemId,
      title: relatedProblem.title,
      estimatedMinutes: relatedProblem.estimatedMinutes,
      eloRating: relatedProblem.eloRating,
      target: { weekIdx, dayIdx },
    })
  }

  const onReplaceRelated = async (
    weekIdx: number,
    dayIdx: number,
  ): Promise<CardDetailsSaveResult> => {
    if (
      !relatedProblem?.learningItemId ||
      detailsCardId === null ||
      !curriculum.replaceWithRelatedProblem
    ) {
      return RELATED_WRITE_NOT_BUILT
    }
    return curriculum.replaceWithRelatedProblem({
      cardId: detailsCardId,
      learningItemId: relatedProblem.learningItemId,
      title: relatedProblem.title,
      weekIdx,
      dayIdx,
    })
  }

  const onPlace = (cardId: string, slotId: string): void => {
    const routing = resolveCurriculumDrop(cardId, slotId, curriculum)
    if (!routing) return
    if (routing.kind === 'place') {
      curriculum.placeCardAt(routing.cardId, routing.weekIdx, routing.dayIdx)
      return
    }
    curriculum.moveCardTo(routing.cardId, routing.weekIdx, routing.dayIdx)
  }

  return (
    // The schedule is the last thing on the page, and the shell's 24px py-6 is
    // the only thing under it — so the final week's cards sit flush against the
    // viewport bottom with nothing to scroll into. The filler gives the page
    // somewhere to scroll past the end of the component.
    <div className="space-y-4 pb-24">
      <FlashNote
        message={curriculum.flash}
        onDismiss={curriculum.dismissFlash}
      />
      {curriculum.retryStage && curriculum.retryStageLabel ? (
        <button
          type="button"
          onClick={curriculum.retryStage}
          className="rounded-full border border-(--cd-border-2) px-3 py-1 text-xs font-semibold text-(--cd-text-muted)"
        >
          Retry staging {curriculum.retryStageLabel}
        </button>
      ) : null}
      <CardBoard
        onPlace={onPlace}
        onDragStartCard={() => curriculum.cancelPlacing()}
        renderOverlay={(cardId) => {
          const found = findDragCard(cardId)
          if (!found) return null
          return <DragCardFace card={found.card} source={found.source} />
        }}
      >
        {/* The row height is a CSS variable rather than an inline `height`
            so it can be gated to `xl:` like the column split — below that
            breakpoint the two regions stack and each keeps its own natural
            height, with no divider to drag. It sits outside the `--cd-*`
            namespace on purpose: that prefix is the dashboard's theme
            palette, which `token-integrity.test.ts` requires be declared in
            index.css, and this is a per-instance layout value. */}
        <div
          ref={containerRef}
          data-testid="curriculum-row"
          className="flex flex-col gap-4 xl:grid xl:h-(--curriculum-row-h) xl:min-h-0"
          style={{
            // The assertion is confined to the custom property: widened over
            // the whole literal it would also switch off checking of
            // `gridTemplateColumns`, where a typo compiles clean and silently
            // collapses the column split to one track.
            ...({
              '--curriculum-row-h': `${libraryHeight}px`,
            } as CSSProperties),
            gridTemplateColumns: `minmax(0,${widths.library}fr) minmax(0,${widths.tray}fr)`,
          }}
        >
          <div className="min-w-0 xl:flex xl:min-h-0 xl:flex-col">
            <LibraryCard />
          </div>
          <div className="relative min-w-0 xl:flex xl:min-h-0 xl:flex-col">
            {/* Both splitters carry a resting `bg-(--cd-border)` and only
                brighten on hover/focus. The reveal cannot be hover-only: the
                xl: breakpoint these are gated to is one a touchscreen laptop
                or a landscape iPad clears, and there a divider that paints
                nothing until a pointer hovers it is a divider the coach never
                learns exists. */}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize library and staging tray"
              aria-valuenow={Math.round(widths.library)}
              aria-valuemin={LIBRARY_MIN_PCT}
              aria-valuemax={LIBRARY_MAX_PCT}
              aria-valuetext={`${Math.round(widths.library)}% library`}
              tabIndex={0}
              className="absolute top-0 bottom-0 -left-3 hidden w-2 cursor-col-resize touch-none rounded-full bg-(--cd-border) transition-colors hover:bg-(--cd-border-3) focus-visible:bg-(--cd-border-3) active:bg-(--cd-border-3) xl:block"
              {...getResizeHandleProps('library')}
            />
            <StagingTray onOpenDetails={setDetailsCardId} />
          </div>
        </div>
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize library and schedule"
          aria-valuenow={Math.round(libraryHeight)}
          aria-valuemin={LIBRARY_MIN_PX}
          aria-valuemax={libraryMax}
          aria-valuetext={`${Math.round(libraryHeight)} pixels tall`}
          tabIndex={0}
          className="hidden h-2 w-full cursor-row-resize touch-none rounded-full bg-(--cd-border) transition-colors hover:bg-(--cd-border-3) focus-visible:bg-(--cd-border-3) active:bg-(--cd-border-3) xl:block"
          {...getRowHandleProps()}
        />
        <Schedule
          onOpenDetails={setDetailsCardId}
          onOpenReview={onOpenReview}
        />
      </CardBoard>
      <CardDetailsModal
        assignedDateLabel={detailsCard?.assignedDateLabel ?? null}
        card={detailsCard?.card ?? null}
        cardDetails={curriculum.cardDetails}
        cardDetailsError={curriculum.cardDetailsError}
        cardDetailsPending={curriculum.cardDetailsPending}
        cardReview={curriculum.cardReview}
        moveTargets={moveTargets}
        onAssignToDay={onAssignToDay}
        onOpenChange={(open) => {
          if (!open) {
            setDetailsCardId(null)
            // The secondary modal is a sibling of this one, not a child, so
            // nothing unmounts it when this closes and its own `open` is
            // driven by an id that no close path touched. Left set, it would
            // stay on screen over a closed card — and pop open by itself the
            // next time a card whose related list contains that same problem
            // was opened. Same stuck-state shape as `sourceSwitcherExpanded`
            // in card-details-modal.tsx:600-610, one level up.
            setRelatedProblemId(null)
          }
        }}
        onOpenRelated={setRelatedProblemId}
        onSaveCoachNote={curriculum.saveCoachNote}
        onSaveSource={curriculum.saveSource}
        onSaveUniversalNotes={curriculum.saveUniversalNotes}
        onSetCompletion={curriculum.setAssignmentCompletion}
        // Whether the dialog is open is the coach's intent, so it is read from
        // the id they picked — not from whether the lookup still finds a card.
        // Every detail write refreshes the plan, which drops any page the coach
        // paged in; deriving `open` from the lookup would make the dialog
        // vanish mid-edit the instant a save landed, and silently re-open later
        // when that page came back.
        open={detailsCardId !== null}
      />
      {/* §8. A sibling of `CardDetailsModal`, never nested inside its
          `DialogContent` — there is no dialog-from-dialog anywhere else in
          this repo, and the tab already owns every assign decision
          (`onAssignToDay` above, `resolveCurriculumDrop`), so putting the
          second dialog here keeps both modals presentational leaves. */}
      <RelatedProblemModal
        // See `relatedModalKey` above for why this is `id:openSequence`
        // rather than `relatedProblem?.problemId ?? 'none'` (round 1) or
        // the id alone (round 2's under-fix).
        key={relatedModalKey}
        moveTargets={relatedMoveTargets}
        onAddToTray={onAddRelatedToTray}
        onAssignToDay={onAssignRelatedToDay}
        onOpenChange={(open) => {
          if (!open) setRelatedProblemId(null)
        }}
        // `null` on the mock (non-persisted) slice — the modal renders the
        // button disabled with the reason rather than omitting it, which
        // would read as "nothing to swap" instead of "not built yet".
        onReplace={
          curriculum.replaceWithRelatedProblem ? onReplaceRelated : null
        }
        open={relatedProblemId !== null}
        related={relatedProblem}
      />
    </div>
  )
}
